// ============================================================================
// OBT — Kampanya HEDEFLERİ (kim cevaplıyor) diyaloğu
// ============================================================================
// Backend sözleşmesi: GET /obt/campaigns/{id}/respondents (RespondentStatusPage).
// `pending`/`late`'ten farkı: sorumlu-park=0 hedefler dahil HİÇBİRİ filtrelenmez;
// böylece yayınlayan "N hedef" sayısının ARKASINDAKİ kişileri görebilir.
//
// Kritik gösterge: `responsible_park_count === 0` olan bir hedef, bu kampanyada
// cevaplayabileceği asli parkı olmayan kişidir (ör. park sahibi olmayan üst amir
// hedeflenmiş). Bu durum kullanıcıya AÇIKÇA uyarı olarak gösterilir — "hedef ama
// cevaplayamaz" tutarsızlığı görünür kılınır.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  Loader2,
  RefreshCw,
  Send,
} from 'lucide-react';
import {
  ObtLoading,
  ObtErrorState,
  ObtEmptyState,
  ObtStatusBadge,
} from '../ObtStates';
import { RESPONSE_STATUS_LABELS, campaignPhase } from '../obtEnums';
import { roleLabel, questionTypeLabel } from './campaignEnums';
import {
  fetchCampaignRespondents,
  fetchRespondentDetail,
  getCampaignQuestions,
  getCampaign,
  downloadCampaignResultXlsx,
} from './campaignApi';
import { normalizeObtError } from '../obtErrors';
import PhotoThumb from '../response/PhotoThumb';

function fmtDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('tr-TR', {
    timeZone: 'Europe/Istanbul',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function SummaryItem({ label, children, wrap = false }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
        {label}
      </dt>
      {/* Tarih gibi uzun değerler kırpılmasın (14 Ağustos 2026 14:31 tam görünsün);
          kısa değerler tek satırda kalır. */}
      <dd
        className={
          wrap
            ? 'whitespace-normal break-words text-sm text-gray-800'
            : 'truncate text-sm text-gray-800'
        }
      >
        {children}
      </dd>
    </div>
  );
}

/** Tek soru cevabını okunur metne çevirir (boolean/çoktan seçmeli/metin). */
function answerText(a) {
  if (a?.answer_boolean === true) return 'Evet';
  if (a?.answer_boolean === false) return 'Hayır';
  if (Array.isArray(a?.option_values) && a.option_values.length > 0) {
    return a.option_values.join(', ');
  }
  if (a?.answer_text) return a.answer_text;
  return '—';
}

/** Revizyon konuşmasındaki bir olayın Türkçe etiketi (kim→kime). */
function revisionKindLabel(h) {
  switch (h?.kind) {
    case 'revision_requested':
      return 'BM revizyon istedi (Formene)';
    case 'my_bounced':
      return 'MY geri gönderdi (BM’ye)';
    case 'rejected':
      return 'BM reddetti';
    case 'resubmitted':
      return 'Formen yeniden gönderdi';
    default:
      return h?.kind || 'İşlem';
  }
}

/** Bir hedefin park cevaplarının açılır detayı (park/soru/cevap/fotoğraf). */
function RespondentDetailBody({ campaignId, personnelId }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    if (!campaignId || !personnelId) return undefined;
    const controller = new AbortController();
    setState({ loading: true, error: null, data: null });
    fetchRespondentDetail(campaignId, personnelId, { signal: controller.signal })
      .then((data) => setState({ loading: false, error: null, data }))
      .catch((e) => {
        if (controller.signal.aborted) return;
        setState({ loading: false, error: normalizeObtError(e), data: null });
      });
    return () => controller.abort();
  }, [campaignId, personnelId]);

  if (state.loading) {
    return (
      <div className="flex items-center gap-2 py-3 pl-2 text-xs text-gray-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        Cevaplar yükleniyor…
      </div>
    );
  }
  if (state.error) {
    return (
      <p className="py-3 pl-2 text-xs text-red-600" role="alert">
        {state.error.message || 'Cevap detayı yüklenemedi.'}
      </p>
    );
  }
  const responses = state.data?.responses || [];
  if (responses.length === 0) {
    return (
      <p className="py-3 pl-2 text-xs text-gray-500">
        Bu kişinin bu bilgi talebinde henüz cevabı yok.
      </p>
    );
  }
  return (
    <div className="space-y-3 py-2 pl-2">
      {responses.map((r) => (
        <div
          key={r.response_id}
          className="rounded-lg border border-gray-200 bg-white p-3"
        >
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">
              {r.park_name || 'Park'}
            </span>
            <ObtStatusBadge value={r.status} labelMap={RESPONSE_STATUS_LABELS} />
          </div>
          {/* Revizyon KONUŞMASI — kronolojik (madde 2): BM→Formen, MY→BM, ret,
              Formen yanıtı; kim/kime/gerekçe. */}
          {Array.isArray(r.revision_history) && r.revision_history.length > 0 && (
            <div className="mb-2 rounded-md bg-amber-50 p-2 text-xs text-amber-900 ring-1 ring-inset ring-amber-200">
              <p className="mb-1 font-semibold">Revizyon geçmişi</p>
              <ul className="space-y-1">
                {r.revision_history.map((h, i) => (
                  <li key={i} className="border-l-2 border-amber-300 pl-2">
                    <span className="font-medium">
                      {revisionKindLabel(h)}
                    </span>
                    {h.actor_name ? ` · ${h.actor_name}` : ''}
                    {h.comment ? `: “${h.comment}”` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <ul className="space-y-2">
            {(r.answers || []).map((a) => (
              <li key={a.question_id} className="text-sm">
                <p className="text-gray-700">
                  <span className="text-gray-500">{a.question_text || 'Soru'}:</span>{' '}
                  <span className="font-medium text-gray-900">{answerText(a)}</span>
                </p>
                {Array.isArray(a.photo_ids) && a.photo_ids.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-2">
                    {a.photo_ids.map((pid) => (
                      <PhotoThumb
                        key={pid}
                        responseId={r.response_id}
                        photoId={pid}
                      />
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {Array.isArray(r.other_photo_ids) && r.other_photo_ids.length > 0 && (
            <div className="mt-2">
              <p className="mb-1 text-xs text-gray-500">Diğer fotoğraflar</p>
              <div className="flex flex-wrap gap-2">
                {r.other_photo_ids.map((pid) => (
                  <PhotoThumb key={pid} responseId={r.response_id} photoId={pid} />
                ))}
              </div>
            </div>
          )}
          {r.response_text && (
            <p className="mt-2 text-xs text-gray-600">Not: {r.response_text}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function TargetRow({ item, campaignId }) {
  const person = item?.person || {};
  const responsible = item?.responsible_park_count ?? 0;
  const answered = item?.answered_park_count ?? 0;
  const draft = item?.draft_park_count ?? 0;
  // Cevaplayamaz: bu kampanyada asli sorumlu olduğu park yok.
  const cannotAnswer = responsible === 0;
  const fullyAnswered = responsible > 0 && answered >= responsible;
  // Detay (park/soru/cevap/foto) yalnız EN AZ 1 cevap (taslak dahil) varsa açılır.
  const hasAny = answered > 0 || draft > 0;
  const [open, setOpen] = useState(false);

  const badge = cannotAnswer ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-300">
      <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
      Cevaplayabileceği park yok
    </span>
  ) : fullyAnswered ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-300">
      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
      {answered}/{responsible} park cevaplandı
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-300">
      <Clock className="h-3.5 w-3.5" aria-hidden="true" />
      {answered}/{responsible} cevaplandı
      {draft > 0 ? ` · ${draft} taslak` : ''}
    </span>
  );

  return (
    <li className="border-b border-gray-100 py-2.5 last:border-b-0">
      {hasAny ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between gap-2 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          aria-expanded={open}
          title="Cevap detaylarını göster/gizle"
        >
          <div className="flex min-w-0 items-center gap-2">
            {open ? (
              <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-gray-900">
                {person.name || 'İsimsiz personel'}
              </p>
              <p className="text-xs text-gray-500">{roleLabel(person.role)}</p>
            </div>
          </div>
          <span className="flex-shrink-0">{badge}</span>
        </button>
      ) : (
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 pl-6">
            <p className="truncate text-sm font-medium text-gray-900">
              {person.name || 'İsimsiz personel'}
            </p>
            <p className="text-xs text-gray-500">{roleLabel(person.role)}</p>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">{badge}</div>
        </div>
      )}
      {hasAny && open && (
        <RespondentDetailBody
          campaignId={campaignId}
          personnelId={person.personnel_id}
        />
      )}
    </li>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {{campaign_id:string, title?:string}|null} props.campaign
 * @param {() => void} props.onClose
 */
export default function CampaignTargetsDialog({ open, campaign, onClose }) {
  const campaignId = campaign?.campaign_id || null;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [delegates, setDelegates] = useState([]);
  const [questions, setQuestions] = useState([]);
  // Taze kampanya özeti (sayaçlar) — liste kartından gelen `campaign` prop'u
  // BAYAT olabilir (dialog açıkken yeni cevap gelirse). Başlık bunu kullanır.
  const [summary, setSummary] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const handleDownloadXlsx = async () => {
    if (!campaignId || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadCampaignResultXlsx(campaignId, { title: campaign?.title });
    } catch (e) {
      setDownloadError(normalizeObtError(e));
    } finally {
      setDownloading(false);
    }
  };

  // Tüm dialog verisini (respondent + dağıtıcı + soru + taze sayaç) yükler.
  // Hem açılışta hem "Yenile"de çağrılır → dialog açıkken gelen yeni cevaplar
  // rozet/başlığa yansır (bayat sayaç sorununu çözer).
  const loadAll = useCallback(
    (signal) => {
      if (!campaignId) return;
      setLoading(true);
      setError(null);
      setQuestions([]);
      fetchCampaignRespondents(campaignId, { limit: 200 }, { signal })
        .then((data) => {
          setItems(Array.isArray(data?.items) ? data.items : []);
          setDelegates(Array.isArray(data?.delegates) ? data.delegates : []);
          setLoading(false);
        })
        .catch((e) => {
          if (signal?.aborted) return;
          setError(normalizeObtError(e));
          setLoading(false);
        });
      // Sorular + taze kampanya sayaçları ayrı çekilir (başarısızlıkları respondent
      // akışını ETKİLEMEZ; yalnız "ne soruldu" ve güncel hedef/cevap sayısı içindir).
      getCampaignQuestions(campaignId, { signal })
        .then((data) => setQuestions(Array.isArray(data?.questions) ? data.questions : []))
        .catch(() => {});
      getCampaign(campaignId, { signal })
        .then((data) => setSummary(data || null))
        .catch(() => {});
    },
    [campaignId]
  );

  useEffect(() => {
    if (!open || !campaignId) return undefined;
    const controller = new AbortController();
    setSummary(null);
    loadAll(controller.signal);
    return () => controller.abort();
  }, [open, campaignId, loadAll]);

  const unanswerable = items.filter(
    (i) => (i?.responsible_park_count ?? 0) === 0
  ).length;

  // Başlık sayaçları TAZE özetten (yoksa liste prop'undan) beslenir.
  const view = summary || campaign || {};

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-2">
            <DialogTitle className="flex flex-wrap items-center gap-2">
              <span className="truncate">
                {view.title || campaign?.title || 'Bilgi talebi'}
              </span>
              {(view.status || campaign?.status) && (
                <ObtStatusBadge {...campaignPhase(view.status ? view : campaign)} />
              )}
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => loadAll()}
              disabled={loading}
              title="Sayaçları ve cevapları yenile"
              className="flex-shrink-0 text-gray-500"
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
                aria-hidden="true"
              />
              <span className="ml-1 hidden sm:inline">Yenile</span>
            </Button>
          </div>
          <DialogDescription>
            Yöneltilen kişiler ve cevap durumları.
          </DialogDescription>
        </DialogHeader>

        {/* Özet detay — sayaçlar TAZE özetten (bayat prop'tan değil). */}
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg bg-gray-50 p-3 sm:grid-cols-3">
          {view.topic ? (
            <SummaryItem label="Konu">{view.topic}</SummaryItem>
          ) : null}
          <SummaryItem label="Oluşturan">
            {view.created_by?.name || '—'}
          </SummaryItem>
          <SummaryItem label="Hedef / Cevap">
            {(view.target_count ?? 0)} hedef · {(view.submitted_response_count ?? 0)} cevap
          </SummaryItem>
          <SummaryItem label="Oluşturma" wrap>
            {fmtDateTime(view.created_at) || '—'}
          </SummaryItem>
          <SummaryItem label="Son cevap tarihi" wrap>
            {fmtDateTime(view.response_due_at) || 'Süresiz'}
          </SummaryItem>
        </dl>

        {/* Sorulan soru(lar) — Müdür/sahibi "ne soruldu"yu görsün. */}
        {questions.length > 0 && (
          <div className="rounded-lg border border-gray-200 p-3">
            <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">
              Sorulan sorular ({questions.length})
            </p>
            <ol className="space-y-1.5">
              {questions.map((q, idx) => (
                <li key={q.question_id || idx} className="text-sm text-gray-800">
                  <span className="mr-1 font-medium text-gray-500">{idx + 1}.</span>
                  {q.question_text}
                  <span className="ml-1 text-xs text-gray-400">
                    ({questionTypeLabel(q.question_type)}
                    {q.is_required ? ', zorunlu' : ''}
                    {q.photo_required ? ', fotoğraf zorunlu' : ''})
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {loading && <ObtLoading label="Hedefler yükleniyor…" />}
        {!loading && error && (
          <ObtErrorState error={error} onRetry={() => loadAll()} />
        )}
        {/* Dağıtıcılar (delegate BM/MY) — kim dağıttı/dağıtacak. Cevaplayanlardan
            ayrı; "dağıtım görevlisi" rozetiyle. */}
        {!loading && !error && delegates.length > 0 && (
          <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3">
            <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-blue-500">
              Dağıtıcılar ({delegates.length})
            </p>
            <ul className="space-y-1.5">
              {delegates.map((d, idx) => (
                <li
                  key={d.personnel_id || `deleg-${idx}`}
                  className="flex items-center justify-between gap-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {d.name || 'İsimsiz personel'}
                    </p>
                    <p className="text-xs text-gray-500">{roleLabel(d.role)}</p>
                  </div>
                  <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-300">
                    <Send className="h-3 w-3" aria-hidden="true" />
                    Dağıtım görevlisi
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!loading && !error && items.length === 0 && delegates.length === 0 && (
          <ObtEmptyState
            title="Hedef bulunmuyor"
            description="Bu bilgi talebinin görebildiğiniz bir hedefi yok."
          />
        )}
        {!loading && !error && items.length > 0 && (
          <div className="space-y-3">
            {delegates.length > 0 && (
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                Cevaplayanlar ({items.length})
              </p>
            )}
            {unanswerable > 0 && (
              <div
                role="note"
                className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 ring-1 ring-inset ring-amber-200"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span>
                  {unanswerable} hedef bu bilgi talebini <strong>doğrudan
                  cevaplayamaz</strong> (asli parkı yok). Üst amirler (Bölge
                  Mühendisi/Müdür Yardımcısı) <strong>dağıtım görevlisidir</strong>:
                  kendi panellerindeki “Dağıtım Görevleri”nden ilgili Formenlerine
                  dağıtır; cevaplar dağıtımdan sonra park sahibi{' '}
                  <strong>Formenlerden</strong> gelir.
                </span>
              </div>
            )}
            <ul>
              {items.map((item, idx) => (
                <TargetRow
                  key={item?.person?.personnel_id || `target-${idx}`}
                  item={item}
                  campaignId={campaignId}
                />
              ))}
            </ul>
          </div>
        )}

        {!loading && !error && (
          <DialogFooter className="mt-2 flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
            {downloadError ? (
              <span className="text-xs text-red-600" role="alert">
                {downloadError.message || 'Excel indirilemedi.'}
              </span>
            ) : (
              <span className="text-xs text-gray-400">
                Excel: Özet + kişi-bazında durum
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadXlsx}
              disabled={downloading}
            >
              {downloading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              Excel İndir
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
